import { createClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from './info';
import { HybridAuthStorage } from './auth-storage';

// ============================================================
// SUPABASE CLIENT SINGLETON
// ============================================================

const supabaseUrl = `https://${projectId}.supabase.co`;

// Use hybrid storage to handle localStorage issues on some hosting platforms
const authStorage = new HybridAuthStorage();

// Create singleton Supabase client with custom storage for better compatibility
export const supabase = createClient(supabaseUrl, publicAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: authStorage as any,
    storageKey: 'supabase.auth.token'
  },
  realtime: {
    params: {
      eventsPerSecond: 10
    }
  }
});

// ============================================================
// DATABASE TYPES
// ============================================================

export type Profile = {
  id: string;
  role: 'teacher' | 'student';
  full_name: string;
  avatar_url: string | null;
  created_at: string;
};

export type Classroom = {
  id: string;
  teacher_id: string;
  title: string;
  description: string | null;
  created_at: string;
};

export type Lesson = {
  id: string;
  teacher_id: string;
  classroom_id: string | null;
  title: string;
  description: string | null;
  mode: 'live' | 'student-paced';
  status: 'draft' | 'published';
  cover_image: string | null;
  created_at: string;
  updated_at: string;
};

export type SlideType =
  | 'text_image'
  | 'video'
  | 'pdf'
  | 'web_content'
  | 'field_trip'
  | 'multiple_choice'
  | 'poll'
  | 'open_ended'
  | 'matching_pairs'
  | 'time_to_climb'
  | 'draw_it'
  | 'collaborate_board'
  | 'word_cloud'
  | 'fill_blanks'
  | 'true_false'
  | 'quiz'
  | 'drag_drop'
  | 'audio'
  | 'label_diagram'
  | 'sequencing'
  | 'text_only';

export type Slide = {
  id: string;
  lesson_id: string;
  type: SlideType;
  content: Record<string, any>;
  slide_order: number;
  created_at: string;
};

export type Session = {
  id: string;
  lesson_id: string;
  teacher_id: string;
  session_code: string;
  mode: 'live' | 'student-paced';
  current_slide: number;
  status: 'active' | 'ended';
  created_at: string;
  due_date?: string | null;
  ended_at: string | null;
};

export type SessionParticipant = {
  id: string;
  session_id: string;
  student_name: string;
  joined_at: string;
  progress_percentage: number;
  last_active: string;
};

export type Submission = {
  id: string;
  session_id: string;
  slide_id: string;
  student_name: string;
  answer: Record<string, any>;
  score: number | null;
  graded: boolean;
  created_at: string;
  updated_at: string;
};

export type Grade = {
  id: string;
  session_id: string;
  lesson_id: string;
  student_name: string;
  total_score: number;
  max_possible_score: number;
  completion_percentage: number;
  updated_at: string;
};

export type LessonShare = {
  id: string;
  lesson_id: string;
  shared_by_teacher_id: string;
  shared_to_teacher_id: string;
  created_at: string;
};

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Get current authenticated user
 */
export async function getCurrentUser() {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
}

/**
 * Get current session
 */
export async function getCurrentSession() {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  return session;
}

/**
 * Check if user is a teacher
 */
export async function isTeacher(): Promise<boolean> {
  const user = await getCurrentUser();
  if (!user) return false;

  const { data: profile } = await supabase
    .from('profiles')
    .select('role')
    .eq('id', user.id)
    .single();

  return profile?.role === 'teacher';
}